import { Box, Chip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { BuzonCategoriaTribunal, BuzonNucPreference } from '@/shared/types/interfaces/BuzonJudicial.interface';
import CategoriaTribunalSelect from './CategoriaTribunalSelect';

interface SeleccionInstanciaPorCasoListaProps {
  casos: BuzonNucPreference[];
  seleccionPorCaso: Map<number, BuzonCategoriaTribunal[]>;
  onChangeCategoria: (idCaso: number, categorias: BuzonCategoriaTribunal[]) => void;
  filtrarCategoriasPorCaso: (idCaso: number) => BuzonCategoriaTribunal[];
  disabled?: boolean;
}

/**
 * Interfaz "modernizada" del punto 4: cada caso es una tarjeta contenida (NUC como tag arriba, selector
 * de tribunal debajo) en vez de columnas separadas de una tabla clásica. La tarjeta sin categoría
 * seleccionada muestra un borde marcado + texto de ayuda, en vez de dejar que el usuario adivine por
 * qué "Confirmar" está deshabilitado. Si hay más de 2-3 casos, la lista scrollea internamente.
 */
const SeleccionInstanciaPorCasoLista = ({
  casos,
  seleccionPorCaso,
  onChangeCategoria,
  filtrarCategoriasPorCaso,
  disabled,
}: SeleccionInstanciaPorCasoListaProps) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography sx={{ fontSize: '0.85rem', color: '#6b7280' }}>
        {t('buzonJudicial.enableModal.multipleCasesHint', 'Selecciona una o más instancias para cada caso')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
          maxHeight: 320,
          overflowY: 'auto',
          pr: 0.5,
        }}
      >
        {casos.map((caso) => {
          const opciones = filtrarCategoriasPorCaso(caso.idCaso);
          const sinOpciones = opciones.length === 0;
          const seleccionActual = seleccionPorCaso.get(caso.idCaso) ?? [];
          const incompleto = !sinOpciones && seleccionActual.length === 0;
          return (
            <Box
              key={caso.idCaso}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                p: 1.5,
                borderRadius: '12px',
                border: '1.5px solid',
                borderColor: incompleto ? '#ef4444' : '#e5e7eb',
                backgroundColor: '#f9fafb',
                transition: 'border-color 150ms ease',
              }}
            >
              <Chip
                label={caso.nuc}
                size="small"
                sx={{
                  alignSelf: 'flex-start',
                  backgroundColor: '#D4EFFF',
                  color: '#0843A0',
                  fontWeight: 700,
                }}
              />
              <CategoriaTribunalSelect
                options={opciones}
                value={seleccionActual}
                onChange={(categorias) => onChangeCategoria(caso.idCaso, categorias)}
                disabled={disabled || sinOpciones}
              />
              {sinOpciones && (
                <Typography sx={{ fontSize: '0.75rem', color: '#b45309' }}>
                  {t('buzonJudicial.enableModal.noCategoriesForCase', 'Este caso no tiene instancias disponibles')}
                </Typography>
              )}
              {incompleto && (
                <Typography sx={{ fontSize: '0.75rem', color: '#ef4444' }}>
                  {t('buzonJudicial.enableModal.missingCategoryForCase', 'Falta seleccionar la categoría de este caso')}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default SeleccionInstanciaPorCasoLista;
